import { ReactElement, useMemo, useCallback, memo } from 'react';
import { CircularProgress } from '@mui/material';
import {
  Delete as DeleteIcon,
  LinkOff as UnLinkIcon,
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRoute as RouteIcon,
  faClone as DuplicateIcon,
  faBarcode as BardCodeIcon,
  faMapMarkerAlt as FinalizeStopIcon,
  faHistory as MarkAsPendingIcon,
  faFileArrowDown as ExportListIcon,
  faCommentDots as ModifyEvidenceIcon,
  faFileInvoiceDollar as BillingIcon,
} from '@fortawesome/free-solid-svg-icons';
import { isNull } from 'lodash';

import { MenuActionsDataTable } from '~components/index';
import {
  MenuActionsDataTableOptions,
  MenuActionsDataTableOptionExtraItem,
} from '~components/MenuActionsDataTable/types';
import { ShowIcon } from '~components/MenuActionsDataTable/styles';
import { useAppSelector, useTypePartnerCompany } from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { ItemExtended } from '~services/item/types';
import { PATHS } from '~constants/index';
import { isOnlyReadUser, isOnlyCreatorOrder, isAdminUser } from '~utils/user';
import { isPendingOrder, isOnAgendaOrder } from '~utils/order';
import { openUrlInNewTab, getUrlTrackingOrder } from '~utils/commons';
import { replaceParamsPath, getStringifyUrlQueryString } from '~utils/router';
import { isCurrentLoggedCompany } from '~utils/partnerCompany';
import { getFeatureAvailableToDate } from '~utils/feature';
import { isRouteStopOrderFinalized } from '~utils/route';
import { validatePartnerItemCollectStateType } from '~utils/partnerItem';
import {
  PlanFeaturesTypes,
  CollectItemStateTypes,
  RouteItemStateType,
} from '~globals/types/enums';

import {
  DialogOrderSearchDetailsOrderData,
  DialogOrderSearchChangeStateOrderData,
} from './types';
import { ORDER_KEY_ID } from './utils';

interface DialogOrderSearchTableActionsMenuProps {
  data: ItemExtended;
  actions: {
    handleOpenDialogFeedback: (data: {
      stopId: string;
      orderId: string;
    }) => void;
    handleExportTicket: (trackingCode: string) => void;
    handleExportTicketMl: (itemId: string) => void;
    handleOpenDialogFinalizedStop: (data: ItemExtended) => void;
    handleOpenDialogConfirmationMarkAsPending: (itemId: string) => void;
    handleDowloadEvidencesOrderPDF: (trackingCode: string) => void;
    handleOpenDialogModifyEvidence: (data: ItemExtended) => void;
    handleOpenDialogCancelOrder: (data: ItemExtended) => void;
    handleOpenConfirmDeleteOrder: (data: ItemExtended) => void;
    handleOpenDialogDetailsOrder: (
      data: DialogOrderSearchDetailsOrderData,
    ) => void;
    handleOpenDialogChangeStateOrder: (
      data: DialogOrderSearchChangeStateOrderData,
    ) => void;
    handleOpenDialogRebillOrder: (itemId: string) => void;
    handleOpenDialogModifyAssignmentOrder: (itemId: string) => void;
  };
  loadings: {
    loadingExportTicket: boolean;
    loadingExportTicketMl: boolean;
    loadingDowloadEvidencesOrderPDF: boolean;
  };
}

const DialogOrderSearchTableActionsMenu = ({
  data,
  actions,
  loadings,
}: DialogOrderSearchTableActionsMenuProps): ReactElement => {
  const { user } = useAppSelector(selectAuth);
  const { isCarrier } = useTypePartnerCompany();

  const isAdmin = useMemo(() => isAdminUser(user), [user]);
  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isCreatorOrderCurrentUser = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const handleShowOrder = useCallback(() => {
    if (!!data.routeCode && !!data.hasFeedback && !!data.routeItemId) {
      actions.handleOpenDialogFeedback({
        stopId: data.routeItemId as string,
        orderId: data[ORDER_KEY_ID],
      });

      return;
    }

    actions.handleOpenDialogDetailsOrder({
      orderId: data[ORDER_KEY_ID],
      currentTab: null,
    });
  }, [actions, data]);

  const items = useMemo<MenuActionsDataTableOptions>(() => {
    let enabledEdit = !isUserOnlyReader && data.canModify;

    if (!isPendingOrder(data)) {
      enabledEdit =
        !isUserOnlyReader && !isCreatorOrderCurrentUser && data.canModify;
    }

    return {
      show: {
        enabled: true,
        divider: !enabledEdit,
        onClick: handleShowOrder,
      },
      edit: {
        enabled: enabledEdit,
        divider: true,
        onClick: (path) => {
          openUrlInNewTab(path);
        },
      },
      delete: { enabled: false },
    };
  }, [isUserOnlyReader, data, handleShowOrder, isCreatorOrderCurrentUser]);

  const isOwnItem = useMemo(() => isNull(data.carrierCompanyId), [data]);
  const isOwnCompany = useMemo(
    () =>
      isCurrentLoggedCompany(data.carrierCompanyId, user?.companyId ?? null),
    [data, user],
  );

  const extraItems = useMemo<MenuActionsDataTableOptionExtraItem[]>(() => {
    let enabledChangeOrder = !isUserOnlyReader && isCarrier;
    let excludeStateOptionOnChangeOrder =
      data.collectItemStateTypeId !== null ? undefined : data.itemStateTypeId;
    let enabledShowRoute = false;
    let enabledExportTicket = true;
    let enabledExportTicketMl = data.sourceImportTypeId === 5;
    let enabledDuplicate = !isUserOnlyReader && data.canModify;
    let enabledFinalizedStop =
      !isUserOnlyReader &&
      !isCreatorOrderCurrentUser &&
      data.routeItemStateTypeId === RouteItemStateType.InProgress;
    let enabledMarkAsPending = false;
    let enabledDownloadDeliveryTicket = false;
    let enabledModifyEvidence = false;
    let enabledCancel =
      !isUserOnlyReader &&
      ((isCarrier && !isOwnItem) ||
        validatePartnerItemCollectStateType(data.collectItemStateTypeId, [
          CollectItemStateTypes.ToValidate,
          CollectItemStateTypes.ToCollect,
          CollectItemStateTypes.NotValidated,
        ]));
    let enabledDeleted =
      !isUserOnlyReader &&
      data.canDelete &&
      (isOwnItem ||
        validatePartnerItemCollectStateType(data.collectItemStateTypeId, [
          CollectItemStateTypes.ToValidate,
          CollectItemStateTypes.ToCollect,
          CollectItemStateTypes.NotValidated,
        ]));
    let enableRebill = false;
    let enabledModifyAssignment = !isUserOnlyReader && isCarrier;

    if (!isPendingOrder(data)) {
      enabledChangeOrder = !isUserOnlyReader && isCarrier && !data.routeCode;
      excludeStateOptionOnChangeOrder = data.itemStateTypeId;
      enabledShowRoute =
        !isCreatorOrderCurrentUser && isCarrier && (isOwnItem || isOwnCompany);
      enabledExportTicketMl = false;
      enabledDuplicate =
        !isUserOnlyReader && !isCreatorOrderCurrentUser && data.canModify;
      enabledCancel = false;
      enabledDeleted = false;

      if (!isOnAgendaOrder(data)) {
        enabledExportTicket = false;
        enabledMarkAsPending =
          !isUserOnlyReader && !isCreatorOrderCurrentUser && data.canModify;
        enabledDownloadDeliveryTicket = isRouteStopOrderFinalized(
          data.routeItemStateTypeId as RouteItemStateType,
        );
        enabledModifyEvidence = !isUserOnlyReader && !isCreatorOrderCurrentUser;
        enableRebill =
          isCarrier &&
          isAdmin &&
          data.itemStateTypeId === RouteItemStateType.FinalizedSuccess;
        enabledModifyAssignment =
          !isUserOnlyReader &&
          isCarrier &&
          data.itemStateTypeId !== RouteItemStateType.FinalizedSuccess;
      }
    }

    if (!data.routeCode) {
      enabledShowRoute = false;
      enabledFinalizedStop = false;
      enabledDownloadDeliveryTicket = false;
      enabledModifyEvidence = false;
    }

    return [
      {
        enabled: enabledChangeOrder,
        label: 'Cambiar estado',
        Icon: <ChangeStateIcon color="success" />,
        onClick: () =>
          actions.handleOpenDialogChangeStateOrder({
            itemsIds: [data[ORDER_KEY_ID]],
            excludeStateOption: excludeStateOptionOnChangeOrder,
          }),
        divider: !enabledModifyAssignment,
      },
      {
        enabled: enabledModifyAssignment,
        label: 'Modificar asignación',
        Icon: <ModifyAssignmentIcon color="success" />,
        onClick: () =>
          actions.handleOpenDialogModifyAssignmentOrder(data[ORDER_KEY_ID]),
        divider: true,
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.LimitNotPro,
        },
      },
      {
        enabled: enabledShowRoute,
        label: 'Ver ruta',
        Icon: <ShowIcon />,
        onClick: () => {
          const urlShowRoute = replaceParamsPath(
            PATHS.ROUTES.VEHICLE_PLANNING,
            {
              ':apiTransactionId': data.apiTransactionId as string,
              ':routeId': data.routeId as string,
            },
          );

          openUrlInNewTab(urlShowRoute);
        },
      },
      {
        enabled: true,
        label: 'Ver seguimiento de órden',
        Icon: <FontAwesomeIcon icon={RouteIcon} />,
        onClick: () => {
          openUrlInNewTab(getUrlTrackingOrder(data.trackingCode));
        },
        divider:
          enabledExportTicket ||
          enabledDuplicate ||
          enabledDownloadDeliveryTicket,
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.TrackingPortalModule,
        },
      },
      {
        enabled: enabledExportTicket,
        label: 'Exportar etiqueta',
        Icon: loadings.loadingExportTicket ? (
          <CircularProgress color="primary" disableShrink size={20} />
        ) : (
          <FontAwesomeIcon icon={BardCodeIcon} />
        ),
        onClick: () => actions.handleExportTicket(data.trackingCode),
        divider:
          (enabledDuplicate || enabledDeleted || enabledCancel) &&
          !enabledExportTicketMl,
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmGenerateTickets,
          availableToDate: getFeatureAvailableToDate(
            PlanFeaturesTypes.MgmGenerateTickets,
          ),
        },
      },
      {
        enabled: enabledExportTicketMl,
        label: 'Exportar etiqueta de Mercado Libre',
        Icon: loadings.loadingExportTicketMl ? (
          <CircularProgress color="primary" disableShrink size={20} />
        ) : (
          <FontAwesomeIcon icon={BardCodeIcon} />
        ),
        onClick: () => actions.handleExportTicketMl(data[ORDER_KEY_ID]),
        divider: enabledDuplicate || enabledDeleted || enabledCancel,
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmGenerateTickets,
          availableToDate: getFeatureAvailableToDate(
            PlanFeaturesTypes.MgmGenerateTickets,
          ),
        },
      },
      {
        enabled: enabledDuplicate,
        label: 'Duplicar',
        Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
        onClick: () => {
          const urlDuplcatedOrder = getStringifyUrlQueryString({
            url: PATHS.ORDERS.NEW,
            query: {
              duplicateById: data[ORDER_KEY_ID],
            },
          });

          openUrlInNewTab(urlDuplcatedOrder);
        },
        divider: enabledCancel || enabledDeleted,
      },
      {
        enabled: enabledFinalizedStop,
        label: 'Finalizar parada',
        Icon: <FontAwesomeIcon icon={FinalizeStopIcon} />,
        onClick: () => actions.handleOpenDialogFinalizedStop(data),
      },
      {
        enabled: enabledMarkAsPending,
        label: 'Marcar como pendiente',
        Icon: <FontAwesomeIcon icon={MarkAsPendingIcon} />,
        onClick: () =>
          actions.handleOpenDialogConfirmationMarkAsPending(data[ORDER_KEY_ID]),
        divider: true,
      },
      {
        enabled: enabledDownloadDeliveryTicket,
        label: 'Descargar comprobante de visita',
        Icon: loadings.loadingDowloadEvidencesOrderPDF ? (
          <CircularProgress color="primary" disableShrink size={20} />
        ) : (
          <FontAwesomeIcon icon={ExportListIcon} />
        ),
        onClick: () =>
          actions.handleDowloadEvidencesOrderPDF(data.trackingCode),
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.TrackingEPOD,
        },
      },
      {
        enabled: enabledModifyEvidence,
        label: 'Modificar evidencia',
        Icon: <FontAwesomeIcon icon={ModifyEvidenceIcon} />,
        onClick: () => actions.handleOpenDialogModifyEvidence(data),
      },
      {
        enabled: enableRebill,
        label: 'Retarifar',
        Icon: <FontAwesomeIcon icon={BillingIcon} />,
        onClick: () => actions.handleOpenDialogRebillOrder(data[ORDER_KEY_ID]),
        validSubscriptionFeature: {
          validFeature: PlanFeaturesTypes.MgmBillingGiver,
          availableToDate: getFeatureAvailableToDate(
            PlanFeaturesTypes.MgmBillingGiver,
          ),
        },
      },
      {
        enabled: enabledCancel,
        label: 'Desasignar colecta',
        Icon: <UnLinkIcon color="error" />,
        onClick: () => actions.handleOpenDialogCancelOrder(data),
      },
      {
        enabled: enabledDeleted,
        label: 'Eliminar',
        Icon: <DeleteIcon color="error" />,
        onClick: () => actions.handleOpenConfirmDeleteOrder(data),
      },
    ];
  }, [
    data,
    isAdmin,
    isCarrier,
    isOwnCompany,
    isOwnItem,
    isUserOnlyReader,
    isCreatorOrderCurrentUser,
    actions,
    loadings,
  ]);

  return (
    <MenuActionsDataTable
      resource={{
        path: PATHS.ORDERS.BASE,
        id: data[ORDER_KEY_ID],
      }}
      items={items}
      extraItems={extraItems}
    />
  );
};

export default memo(DialogOrderSearchTableActionsMenu);
